import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { 
  FiHome, 
  FiPackage, 
  FiShoppingBag, 
  FiUsers, 
  FiStar, 
  FiGrid,
  FiSettings,
  FiBarChart2,
  FiShield,
  FiMenu,
  FiX,
  FiLogOut,
  FiGlobe,
  FiUser
} from 'react-icons/fi';
import useAuthStore from '../store/useAuthStore';

const AdminLayout = () => {
  const location = useLocation();
  const { logout, user } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { path: '/admin', icon: FiHome, label: 'Dashboard' },
    { path: '/admin/reports', icon: FiBarChart2, label: 'Raporlar' },
    { path: '/admin/products', icon: FiPackage, label: 'Ürünler' },
    { path: '/admin/categories', icon: FiGrid, label: 'Kategoriler' },
    { path: '/admin/collections', icon: FiGrid, label: 'Koleksiyonlar' },
    { path: '/admin/campaigns', icon: FiStar, label: 'Kampanyalar' },
    { path: '/admin/orders', icon: FiShoppingBag, label: 'Siparişler' },
    { path: '/admin/customers', icon: FiUsers, label: 'Müşteriler' },
    { path: '/admin/reviews', icon: FiStar, label: 'Yorumlar' },
    { path: '/admin/settings', icon: FiSettings, label: 'Ayarlar' },
    { path: '/admin/security', icon: FiShield, label: 'Güvenlik & Firewall' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased text-slate-800">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Fixed Full-Height Dark Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 h-screen w-64 bg-slate-900 text-slate-300 shadow-2xl z-50
          flex flex-col justify-between flex-shrink-0
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Brand Header */}
        <div className="p-4 border-b border-slate-800/80 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5 group">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-tr from-amber-500 to-amber-600 text-white font-bold text-xl flex items-center justify-center shadow-md shadow-amber-500/30 group-hover:scale-105 transition">
              M
            </span>
            <span className="text-xl font-extrabold tracking-tight text-white group-hover:text-amber-400 transition">
              Mashow <span className="text-amber-500 font-medium text-xs px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700/60 ml-0.5">Admin</span>
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white p-1"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="p-3 space-y-1 overflow-y-auto flex-1 py-4">
          <div className="px-3 pb-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Yönetim Menüsü
          </div>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${
                  isActive
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold shadow-md shadow-amber-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/80'
                }`}
              >
                <Icon className={`text-base flex-shrink-0 transition-transform group-hover:scale-110 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-amber-400'}`} />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Footer Status */}
        <div className="p-3.5 border-t border-slate-800/80 bg-slate-950/50 flex-shrink-0">
          <div className="flex items-center gap-2.5 px-2 py-0.5 text-xs text-slate-400 font-medium">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Sistem Aktif (v1.0)</span>
          </div>
        </div>
      </aside>

      {/* Main Container Pushed Right */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top Sticky Header */}
        <header className="bg-white/90 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-xs">
          <div className="px-4 lg:px-6 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile Hamburger Button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition"
                aria-label="Toggle Navigation"
              >
                <FiMenu className="text-2xl" />
              </button>
            </div>

            <div className="flex items-center gap-3">
              <Link 
                to="/" 
                className="text-xs md:text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition flex items-center gap-2 shadow-xs"
              >
                <FiGlobe className="text-amber-600 text-base" />
                <span className="hidden sm:inline">Siteye Dön</span>
              </Link>

              <div className="h-5 w-px bg-slate-200 hidden sm:block"></div>

              <div className="flex items-center gap-2 bg-slate-100/80 p-1.5 rounded-xl border border-slate-200/60">
                <div className="w-8 h-8 rounded-lg bg-slate-900 text-amber-400 flex items-center justify-center text-xs font-bold shadow-xs">
                  {user?.firstName?.charAt(0) || <FiUser />}
                </div>
                <span className="text-xs md:text-sm font-semibold text-slate-800 pr-2 hidden sm:inline">
                  {user?.firstName}
                </span>
                <button
                  onClick={logout}
                  className="text-slate-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-white transition"
                  title="Çıkış Yap"
                >
                  <FiLogOut className="text-base" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 w-full min-w-0 flex flex-col justify-between overflow-x-hidden">
          <div className="flex-1 space-y-6">
            <Outlet />
          </div>
          <footer className="mt-10 pt-6 border-t border-slate-200/80 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2">
            <p>© {new Date().getFullYear()} Mashow Kuyumculuk Yönetim Paneli. Tüm hakları saklıdır.</p>
            <div className="flex items-center gap-4">
              <span className="hover:text-slate-600 cursor-pointer">Güvenli Yönetim Altyapısı</span>
              <span>•</span>
              <span className="hover:text-slate-600 cursor-pointer">v1.0.4 Live</span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

